import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PageLayout from '../components/PageLayout';
import { requestPasswordChangeOTP, changePassword } from '../services/api';
import { toast } from 'react-toastify';

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 24px;
`;

const ProfileCard = styled.div`
  display: flex;
  width: 100%;
  max-width: 750px;
  background-color: #1E1E1E;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-height: 400px;
  margin: 40px auto 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    min-height: auto;
    box-shadow: none;
  }
`;

const Sidebar = styled.aside`
  flex-basis: 280px;
  flex-shrink: 0;
  background: linear-gradient(to bottom, #2c2c2c, #1a1a1a);
  padding: 30px 20px;
  color: #e0e0e0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-basis: auto;
    width: 100%;
    padding: 25px 20px;
    border-radius: 0;
  }
`;

const ProfilePicture = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #4a4a4a;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 15px;
  overflow: hidden;
  
  svg {
    width: 60px;
    height: 60px;
    fill: #d0d0d0;
  }
`;

const UserName = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const ContactInfo = styled.p`
  font-size: 13px;
  color: #b0b0b0;
  margin: 4px 0;
  word-break: break-all;
`;

const EditProfileBtn = styled.button`
  background-color: transparent;
  border: 1px solid #5a5a5a;
  color: #d0d0d0;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-top: auto;
  transition: background-color 0.3s, border-color 0.3s;
  
  &:hover {
    background-color: #333333;
    border-color: #777777;
  }
  
  @media (max-width: 768px) {
    margin-top: 25px;
    width: 80%;
    max-width: 250px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 15px;
    font-size: 13px;
  }
`;

const MainContent = styled.main`
  flex-grow: 1;
  background-color: #ffffff;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  
  @media (max-width: 768px) {
    padding: 25px 20px;
    justify-content: flex-start;
    gap: 30px;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    padding: 20px 15px;
  }
`;

const TopStats = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: flex-start;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 25px;
    margin-bottom: 0;
  }
`;

const BottomStats = styled.div`
  display: flex;
  justify-content: center;
  
  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 10px;
  min-width: 120px;
  
  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
    max-width: 280px;
    padding: 15px 10px;
  }
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #111111;
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #666666;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const LabelWithIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CoinIcon = styled.div`
  width: 20px;
  height: 20px;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const StatItemLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`;

const ReferralButton = styled(Link)`
  display: block;
  margin-top: 15px;
  padding: 8px 15px;
  border: 1px solid #5a5a5a;
  background-color: transparent;
  color: #d0d0d0;
  border-radius: 6px;
  text-align: center;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s, border-color 0.3s;
  
  &:hover {
    background-color: #333333;
    border-color: #777777;
  }
  
  @media (max-width: 768px) {
    width: 80%;
    max-width: 250px;
  }
`;

const ChangePasswordDialog = ({ isOpen, onClose, user }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [step, setStep] = useState('request'); // 'request' or 'verify'
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const isPasswordSetup = user?.googleId && !user?.password; // Check if user is setting password for first time

  useEffect(() => {
    if (!isOpen) {
      // Reset state when dialog closes
      setStep('request');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccessMessage('');
    }
  }, [isOpen]);

  const handleRequestOTP = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await requestPasswordChangeOTP();
      if (response.success) {
        setSuccessMessage('OTP has been sent to your email. Please check your inbox.');
        setStep('verify');
      } else {
        setError(response.error || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!otp) {
      setError('Please enter the OTP code');
      return;
    }

    setIsLoading(true);

    try {
      const response = await changePassword(otp, newPassword);
      if (response.success) {
        toast.success('Password changed successfully! You will be logged out. Please log in again with your new password.');
        // Logout user after 2 seconds
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        setError(response.error || 'Failed to change password');
      }
    } catch (err) {
      setError('Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isPasswordSetup ? 'Set Password' : 'Change Password'}</DialogTitle>
        </DialogHeader>
        {step === 'request' ? (
          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="text-sm text-yellow-700">
                <strong>Important:</strong> After {isPasswordSetup ? 'setting' : 'changing'} your password, you will be logged out and will need to log in again with your new password.
              </p>
            </div>
            <p className="text-sm text-gray-600">
              Click the button below to receive an OTP code via email. You'll need this code to {isPasswordSetup ? 'set' : 'change'} your password.
            </p>
            {error && (
              <div className="text-sm font-medium text-destructive">
                {error}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="button" onClick={handleRequestOTP} disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send OTP to Email'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4 py-4">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="text-sm text-yellow-700">
                <strong>Important:</strong> After {isPasswordSetup ? 'setting' : 'changing'} your password, you will be logged out and will need to log in again with your new password.
              </p>
            </div>
            {successMessage && (
              <div className="text-sm font-medium text-green-600 bg-green-50 p-3 rounded">
                {successMessage}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="otp">OTP Code</Label>
              <Input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 8-digit OTP code"
                maxLength={8}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
              <p className="text-xs text-muted-foreground">
                Password must be at least 8 characters long
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>
            {error && (
              <div className="text-sm font-medium text-destructive">
                {error}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep('request')}>
                Back
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Changing...' : 'Change Password'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EditProfileDialog = ({ isOpen, onClose, user, onUpdate }) => {
  const { updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // No age validation - just save the date of birth

      const response = await updateProfile(formData);
      if (response.success) {
        onUpdate(response.user);
        onClose();
      } else {
        setError(response.error || 'Failed to update profile');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="firstName" className="text-sm font-medium leading-none">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="John"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="lastName" className="text-sm font-medium leading-none">
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Doe"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="dateOfBirth" className="text-sm font-medium leading-none">
              Date of Birth
            </label>
            <input
              id="dateOfBirth"
              name="dateOfBirth"
              type="date"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={formData.dateOfBirth}
              onChange={handleChange}
              required
            />
            <p className="text-xs text-muted-foreground">
              You will receive birthday surprises in your email!
            </p>
          </div>
          
          {error && (
            <div className="text-sm font-medium text-destructive">
              {error}
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  
  useEffect(() => {
    // Check if the user has default values that need to be updated
    if (user) {
      const needsProfileUpdate = 
        user.firstName === 'User' && 
        user.lastName === 'Account';
      
      setShowProfilePrompt(needsProfileUpdate);
    }
  }, [user]);
  
  const handleProfileUpdate = (updatedUser) => {
    updateUser(updatedUser);
    setShowProfilePrompt(false);
  };

  if (!user) {
    return (
      <PageLayout>
        <ContentWrapper>
          <div className="text-center py-8">Loading profile...</div>
        </ContentWrapper>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <ContentWrapper>
        {showProfilePrompt && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  Please complete your profile information.
                </p>
                <button 
                  className="mt-2 text-sm font-medium text-blue-700 hover:text-blue-600 underline"
                  onClick={() => setIsEditProfileOpen(true)}
                >
                  Update Profile
                </button>
              </div>
            </div>
          </div>
        )}
        
        <ProfileCard>
          <Sidebar>
            <ProfilePicture>
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
              </svg>
            </ProfilePicture>
            <UserName>{user.firstName} {user.lastName}</UserName>
            {user.phone && <ContactInfo>{user.countryCode} {user.phone}</ContactInfo>}
            {user.email && <ContactInfo>{user.email}</ContactInfo>}
            <EditProfileBtn onClick={() => setIsEditProfileOpen(true)}>Edit Profile</EditProfileBtn>
            <EditProfileBtn 
              onClick={() => setIsChangePasswordOpen(true)}
              style={{ marginTop: '10px' }}
            >
              {user.googleId && !user.password ? 'Set Password' : 'Change Password'}
            </EditProfileBtn>
            <ReferralButton to="/referral">Refer & Earn</ReferralButton>
          </Sidebar>
          <MainContent>
            <TopStats>
              <StatItem>
                <StatValue>0</StatValue>
                <StatLabel>MAD Wallet</StatLabel>
              </StatItem>
              <StatItemLink to="/coin-activity">
                <StatItem>
                  <StatValue>{user.madCoins.balance || 0}</StatValue>
                  <LabelWithIcon>
                    <StatLabel>MAD Coins</StatLabel>
                    <CoinIcon>
                      <img src="/images/madicon.png" alt="MAD Coin" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </CoinIcon>
                  </LabelWithIcon>
                </StatItem>
              </StatItemLink>
            </TopStats>
            <BottomStats>
              <StatItemLink to="/addresses">
                <StatItem>
                  <StatValue>{user.addresses?.length || 0}</StatValue>
                  <StatLabel>Saved Addresses</StatLabel>
                </StatItem>
              </StatItemLink>
            </BottomStats>
          </MainContent>
        </ProfileCard>
      
        <EditProfileDialog 
          isOpen={isEditProfileOpen}
          onClose={() => setIsEditProfileOpen(false)}
          user={user}
          onUpdate={handleProfileUpdate}
        />
        <ChangePasswordDialog 
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
          user={user}
        />
      </ContentWrapper>
    </PageLayout>
  );
};

export default ProfilePage;
